import { Grid } from '@material-ui/core'
import React from 'react'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Box } from 'shared/components/Box/Box'
import { Button } from 'shared/components/Button/Button'
import { Text } from 'shared/components/Text/Text'
import { lightGray1, royalBlue } from 'shared/theme/colors'

import { Handler } from '../../helpers/typeHelper'
import { useRepaymentPolicy } from '../../hooks/query/useRepaymentPolicy'
import { useRepaymentPolicyDocuments } from '../../hooks/query/useRepaymentPolicyDocuments'
import { useVendorPrograms } from '../../hooks/query/useVendorPrograms'
import {
  formatSubheaderValue,
  getNextMondayPlus24Hours,
  getPaymentPeriodsFromPolicy,
  parseSubheaderValue,
  RepaymentScheduleType,
  VendorProgramPolicyType,
} from '../../models/repayments'
import { ConsoleTeam } from '../../models/team'
import { PolicyFormDetails } from './PolicyDetail/PolicyFormDetails'
import { PolicyFormPolicyPicker } from './PolicyFormPolicyPicker'

interface PolicyEditProps {
  team: ConsoleTeam
  policyId: string
  teamUrl?: string
  onCancel?: Handler
}

export function PolicyEdit({ team, policyId, onCancel, teamUrl }: PolicyEditProps) {
  const vendorPrograms = useVendorPrograms(team.id)
  const { policy, refetch: refetchPolicy } = useRepaymentPolicy({ teamId: team.id, policyId })
  const { policyDocuments, refetch: refetchDocument } = useRepaymentPolicyDocuments(policyId)

  if (!team || !policy || !policyDocuments) {
    return null
  }

  const activeVendorProgramName = policy.repayment_details.vendor_program_tags?.length
    ? policy.repayment_details.vendor_program_tags[0]
    : undefined
  const activeVendorProgram = vendorPrograms.find(({ name }) => name === activeVendorProgramName)

  if (!activeVendorProgram) {
    return null
  }

  return (
    <div>
      <Box mt={4} />

      <Grid container direction="column" spacing={2} style={{ maxWidth: 1000 }}>
        <FlexBox justifyContent="space-between" alignItems="flex-start">
          <Grid container item xs={6} direction="column">
            <FlexBox alignItems="center" gap="20px" mb={2}>
              <Text variant="header2">Edit {activeVendorProgramName}</Text>
            </FlexBox>

            <FlexBox alignItems="center" gap="20px" mb={4}>
              <Text variant="header3" fontColor={lightGray1}>
                Fleet name
              </Text>
              <Button variant="text" href={teamUrl}>
                <Text variant="header3" fontColor={royalBlue}>
                  {team.name}
                </Text>
              </Button>
            </FlexBox>
          </Grid>

          <Button variant="secondary" onClick={onCancel}>
            CANCEL
          </Button>
        </FlexBox>

        <PolicyFormPolicyPicker
          secondStep={true}
          vendorPrograms={vendorPrograms}
          activeVendorProgramName={activeVendorProgramName}
          setActiveVendorProgramName={() => {}}
          onNext={() => {}}
          isDisabled
        />

        <Box mt={4} />

        <PolicyFormDetails
          secondStep={true}
          activeVendorProgram={activeVendorProgram}
          team={team}
          isEditing
          editingPolicyId={policyId}
          initialValues={{
            // TODO: @taylangocmen form validation before submission
            payment_description:
              policy.repayment_details.policy_description ?? activeVendorProgram.description,
            sub_header:
              policy.repayment_details.policy_sub_header ??
              formatSubheaderValue(activeVendorProgram.subheader, ''),
            start_date: policy.repayment_details.payment_start_date
              ? new Date(policy.repayment_details.payment_start_date)
              : getNextMondayPlus24Hours(),
            advanced_amount:
              policy.repayment_details.total_amount_owed === 'N/A'
                ? undefined
                : parseFloat(policy.repayment_details.total_amount_owed),
            payment_schedule: policy.repayment_details.payment_period_type ?? RepaymentScheduleType.WEEKLY,
            number_of_payment_periods:
              activeVendorProgram.policy_type === VendorProgramPolicyType.SUBSCRIPTION
                ? -1
                : getPaymentPeriodsFromPolicy(policy),
            ...parseSubheaderValue(activeVendorProgram.subheader, policy.repayment_details.policy_sub_header),
          }}
          policyDocuments={policyDocuments}
          refetchDocuments={refetchDocument}
          refetchPolicy={refetchPolicy}
        />
      </Grid>
    </div>
  )
}
